import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ShieldCheck, TriangleAlert } from "lucide-react";
import { api } from "../lib/api";
import { fmtAxv, IMAGES, SUB } from "../lib/tokens";
import { useWorld } from "../state/World";
import Plate from "../components/Plate";

const FIELDS = [
  ["network_fee_sub", "Network fee (XRPL sim)", "locked seed 0.001 AXV"],
  ["endeavorum_fee_sub", "Endeavorum flat fee", "flat, never a percentage"],
  ["cluster_cost_sub", "Table clustering cost", "principal for one MelzAI pass"],
  ["signup_grant_sub", "Signup grant", "genesis mint per new Pouch"],
  ["referral_bonus_sub", "Referral bonus", "paid to the inviter when a Nudger reaches the World"],
  ["token_basis_usd_cents", "Token basis (USD cents)", "public basis, integer cents"],
  ["axr_per_axv_micro", "AXR : AXV (micro)", "internal only — never in public UI"],
];

export default function Admin() {
  const { me } = useWorld();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [swings, setSwings] = useState(null);

  const load = useCallback(async () => {
    const [o, u, l] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/users"),
      api.get("/melzai/live-state"),
    ]);
    setSwings(l.data);
    setData(o.data);
    setUsers(u.data);
    setForm(
      Object.fromEntries(FIELDS.map(([k]) => [k, String(o.data.config[k])]))
    );
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Founder console is role-gated."));
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const patch = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, parseInt(v, 10)])
      );
      await api.patch("/admin/config", patch);
      await load();
      toast.success("Economics updated. Integers only.");
    } catch (e) {
      toast.error("Rejected. Values must be whole sub-units.");
    } finally {
      setBusy(false);
    }
  };

  const grant = async (userId) => {
    try {
      await api.post("/admin/grant", { user_id: userId, amount_sub: 10 * SUB, memo: "Founder grant" });
      await load();
      toast.success("10 Tokens minted into that Pouch.");
    } catch (e) {
      toast.error("Grant failed.");
    }
  };

  if (me && me.user.role !== "founder") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
        <div className="serif text-2xl text-white/70">This door is not for judges.</div>
        <p className="mono max-w-sm text-[11px] leading-relaxed text-white/35">
          The Founder console is role-gated. Enter with founder@endeavorum.world to
          reach it.
        </p>
        <button type="button" onClick={() => nav("/")} className="btn">
          The Atlas
        </button>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Plate src={IMAGES.atlasIslands} depth={5} />

      <div className="relative z-10 mx-auto max-w-5xl px-6 pb-28 pt-20 md:px-10">
        <button
          type="button"
          data-testid="admin-back"
          onClick={() => nav("/")}
          className="tracked mb-12 flex items-center gap-2 text-[9px] text-white/40 transition-colors duration-200 hover:text-[var(--stage-gold)]"
        >
          <ArrowLeft size={12} /> The Atlas
        </button>

        <div className="tracked mb-5 text-[10px] text-[var(--stage-gold)]/70">
          Founder console · not judge-facing
        </div>
        <h1 className="serif text-4xl font-light leading-tight text-[#f4ecdd] sm:text-5xl">
          The rails, from the inside.
        </h1>

        {!data ? (
          <Loader2 className="mt-16 animate-spin text-white/30" />
        ) : (
          <>
            <div className="mono mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["Minted", data.minted_sub, "var(--stage-gold)"],
                ["Treasury", data.treasury_sub, "#e8d8a8"],
                ["Network (sim)", data.network_sub, "var(--arcade-cyan)"],
                ["Users", data.users, "#ffffff99"],
              ].map(([label, v, color]) => (
                <div key={label} className="glass rounded-xl p-5">
                  <div className="tracked mb-2 text-[9px] text-white/40">{label}</div>
                  <div className="text-xl" style={{ color }} data-testid={`admin-stat-${label.toLowerCase().split(" ")[0]}`}>
                    {label === "Users" ? v : fmtAxv(v)}
                  </div>
                </div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mono mt-6 flex items-center gap-3 rounded-xl border p-5 text-[11px] ${
                data.reconciliation.conserved
                  ? "border-emerald-500/25 text-emerald-400"
                  : "border-red-500/30 text-red-400"
              }`}
              data-testid="admin-reconciliation"
            >
              {data.reconciliation.conserved ? <ShieldCheck size={15} /> : <TriangleAlert size={15} />}
              <span>
                {data.reconciliation.transactions} transactions replayed ·{" "}
                {data.reconciliation.conserved
                  ? "conservation invariant holds"
                  : "INVARIANT BROKEN — halt"}
              </span>
            </motion.div>

            <div className="mt-12 glass rounded-2xl p-7" data-testid="admin-config-panel">
              <div className="tracked mb-6 text-[9px] text-white/40">
                Economics · all values in integer sub-units (1 AXV = 1,000,000)
              </div>

              <div
                className="mono mb-8 rounded-xl border border-[var(--stage-gold)]/25 bg-[var(--stage-gold)]/5 p-5 text-[11px]"
                data-testid="fee-schedule-summary"
              >
                <div className="tracked mb-3 text-[9px] text-[var(--stage-gold)]/80">
                  Fee schedule the user actually sees, per transaction
                </div>
                <div className="flex justify-between text-white/45">
                  <span>Network (XRPL sim) — pass-through</span>
                  <span>{fmtAxv(parseInt(form.network_fee_sub, 10) || 0)}</span>
                </div>
                <div className="flex justify-between text-white/45">
                  <span>Endeavorum flat fee — to Treasury</span>
                  <span>{fmtAxv(parseInt(form.endeavorum_fee_sub, 10) || 0)}</span>
                </div>
                <div className="mt-3 flex justify-between border-t border-white/12 pt-3 text-[13px] text-[var(--stage-gold)]">
                  <span className="tracked text-[9px]">Total debited on every move</span>
                  <span data-testid="fee-schedule-total">
                    {fmtAxv(
                      (parseInt(form.network_fee_sub, 10) || 0) +
                        (parseInt(form.endeavorum_fee_sub, 10) || 0)
                    )}
                  </span>
                </div>
                <div className="mt-3 text-[9px] leading-relaxed text-white/30">
                  Flat integers, never a percentage. Both lines are itemized to the user
                  before every spend and on the immutable receipt after it.
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                {FIELDS.map(([k, label, note]) => (
                  <div key={k}>
                    <label className="mono mb-1.5 block text-[10px] text-white/45">{label}</label>
                    <input
                      className="field mono"
                      data-testid={`admin-field-${k}`}
                      value={form[k] ?? ""}
                      onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value.replace(/[^0-9]/g, "") }))}
                    />
                    <div className="mono mt-1 text-[9px] text-white/25">
                      {note}
                      {k.endsWith("_sub") && form[k] ? ` · = ${fmtAxv(parseInt(form[k], 10) || 0)} AXV` : ""}
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                data-testid="admin-save-config"
                disabled={busy}
                onClick={save}
                className="btn btn-gold mt-8"
              >
                {busy ? <Loader2 className="animate-spin" size={14} /> : "Commit economics"}
              </button>
            </div>

            {/* Open-alpha guardrail. Live is real, but rationed per surface. */}
            <div className="mt-8 glass rounded-2xl p-7" data-testid="admin-live-panel">
              <div className="tracked mb-2 text-[9px] text-white/40">
                MelzAI live swings · open alpha guardrail
              </div>
              <p className="mb-6 max-w-lg text-[12px] leading-relaxed text-white/40">
                Every surface gets {swings?.cap_per_surface ?? 2} genuine Claude calls per
                person, then quietly returns to pre-vetted Demo Mode. Live is proven in
                every Space rather than spent in one room. Global backstop:{" "}
                <span className="mono text-[var(--stage-gold)]">
                  {swings?.global_used_day ?? 0}/{swings?.global_cap_day ?? 300}
                </span>{" "}
                today. Rows below are <span className="text-white/60">your own</span>{" "}
                remaining swings; the reset hands them back to everybody.
              </p>
              <div className="mb-6 space-y-2">
                {Object.entries(swings?.surfaces || {}).map(([key, v]) => (
                  <div
                    key={key}
                    className="mono flex flex-wrap items-center justify-between gap-4 border-b border-white/6 pb-2 text-[11px] last:border-0"
                    data-testid={`admin-swing-${key}`}
                  >
                    <span className="text-white/55">{v.label}</span>
                    <span style={{ color: v.spent ? "var(--stage-ember)" : "var(--stage-gold)" }}>
                      {v.spent ? "spent · demo mode" : `${v.left} of ${swings.cap_per_surface} left`}
                    </span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                data-testid="admin-reset-swings"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api.post("/admin/live-swings/reset", {});
                    await load();
                    toast.success("Live swings handed back. Rehearse away.");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not reset.");
                  } finally {
                    setBusy(false);
                  }
                }}
                className="btn btn-gold"
              >
                {busy ? <Loader2 className="animate-spin" size={14} /> : "Hand back all live swings"}
              </button>
            </div>

            <div className="mt-8 glass rounded-2xl p-7" data-testid="admin-users-panel">
              <div className="tracked mb-6 text-[9px] text-white/40">Users & Pouches</div>
              <div className="space-y-2">
                {users.map((u) => (
                  <div
                    key={u.id}
                    className="mono flex flex-wrap items-center justify-between gap-4 border-b border-white/6 pb-2 text-[11px] last:border-0"
                    data-testid="admin-user-row"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-white/65">{u.display_name}</div>
                      <div className="truncate text-[9px] text-white/25">{u.email} · {u.role}</div>
                    </div>
                    <div className="text-[var(--stage-gold)]">{fmtAxv(u.pouch_sub)}</div>
                    <div className="text-[var(--arcade-cyan)]">{fmtAxv(u.stake_sub)}</div>
                    <button
                      type="button"
                      onClick={() => grant(u.id)}
                      data-testid="admin-grant-button"
                      className="rounded-full border border-white/15 px-3 py-1 text-[9px] text-white/45 transition-colors duration-200 hover:border-[var(--stage-gold)]/50 hover:text-[var(--stage-gold)]"
                    >
                      +10
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mono mt-8 rounded-xl border border-white/8 p-5 text-[10px] leading-relaxed text-white/30">
              Valuation rail · token basis {data.valuation.token_basis_usd_cents}¢ per AXV ·
              AXR:AXV {data.valuation.axr_per_axv_micro} micro. {data.valuation.note}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
